import { useState, useMemo } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { conditionGroupSummary, calculateRowReach, type WaterfallRow } from './waterfallTypes';
import type { CampaignMember } from './types';

interface SavedWaterfallAccordionProps {
  rows: WaterfallRow[];
  members: CampaignMember[];
}

export function SavedWaterfallAccordion({ rows, members }: SavedWaterfallAccordionProps) {
  const [open, setOpen] = useState(false);

  const rowReach = useMemo(() => calculateRowReach(members, rows), [members, rows]);

  if (rows.length === 0) return null;

  return (
    <div className="rounded-2xl border border-nightshade-200 dark:border-nightshade-700 overflow-hidden bg-white dark:bg-nightshade-900">
      {/* Toggle header */}
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-2 px-4 py-3 text-left hover:bg-nightshade-50 dark:hover:bg-nightshade-800/50 transition-colors"
      >
        <span className="text-nightshade-400 dark:text-nightshade-500">
          {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </span>
        <span className="text-[11px] font-mono font-medium text-nightshade-500 dark:text-nightshade-400 uppercase tracking-wider">
          Saved configuration
        </span>
        <span className="text-xs text-nightshade-400 dark:text-nightshade-500">
          · {rows.length} row{rows.length !== 1 ? 's' : ''}
        </span>
      </button>

      {/* Row list */}
      {open && (
        <div className="border-t border-nightshade-100 dark:border-nightshade-800">
          {rows.map((row, i) => {
            const reach = rowReach[row.id] ?? 0;
            const enabledCells = Object.values(row.cells).filter(c => c.enabled);
            const allReady = enabledCells.length > 0 && enabledCells.every(c => c.copy_status === 'ready');
            const isLast = i === rows.length - 1;

            return (
              <div
                key={row.id}
                className={`flex items-center gap-3 px-4 py-2.5 ${!isLast ? 'border-b border-nightshade-100 dark:border-nightshade-800' : ''}`}
              >
                <span className="text-sm font-medium text-nightshade-700 dark:text-nightshade-300 w-28 flex-shrink-0 truncate">
                  {row.name || <span className="italic text-nightshade-400 font-normal">Unnamed</span>}
                </span>
                <span className="text-xs text-nightshade-400 dark:text-nightshade-500 flex-1 truncate min-w-0">
                  {conditionGroupSummary(row.condition_groups ?? [], row.group_logic ?? 'AND')}
                </span>
                <span className="text-xs font-semibold text-nightshade-400 dark:text-nightshade-500 tabular-nums flex-shrink-0">
                  {reach}
                </span>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium border flex-shrink-0 ${
                  allReady
                    ? 'bg-matcha-50 dark:bg-matcha-900/20 text-matcha-700 dark:text-matcha-400 border-matcha-200 dark:border-matcha-800'
                    : 'bg-tangerine-50 dark:bg-tangerine-900/20 text-tangerine-700 dark:text-tangerine-400 border-tangerine-200 dark:border-tangerine-800'
                }`}>
                  {allReady ? 'Ready' : 'Draft'}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
